import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { SharedModule } from '@/app/shared/shared.module';
import { GddAnalysisDataViewModel, SatelliteViewModel } from '@/app/proxy/proxies';
import { EChartsOption } from 'echarts';
import { DateTime } from 'luxon';
import { PlantationService } from '@/app/core/services/plantation.service';
import { MixpanelTrackerService } from '@/app/core/analytics';
import { NoAnalysisComponent } from '../no-analysis/no-analysis.component';
import { CropHealthMapType } from '../crop-health/crop-health-map/crop-health-map-footer/crop-health-map-footer.component';
import { LuxonFormatPipe } from '@/app/shared/pipes/luxon-format.pipe';

@Component({
  selector: 'app-heat-accumulation',
  imports: [SharedModule, NoAnalysisComponent],
  templateUrl: './heat-accumulation.component.html',
  styleUrl: './heat-accumulation.component.less',
  providers: [LuxonFormatPipe]
})
export class HeatAccumulationComponent implements OnInit, OnChanges {
  private luxonFormatPipe = inject(LuxonFormatPipe);

  @Input() loading = false;
  @Input() data: GddAnalysisDataViewModel[] = [];
  @Input() selectedDay: SatelliteViewModel;
  @Input() selectedMapType: CropHealthMapType;

  activeTab: 'standard' | 'code-specific' = 'standard';
  chartOption!: EChartsOption;
  lastValue: {
    date: DateTime;
    value: number;
  };

  constructor(
    public plantationService: PlantationService,
    private mixpanelTracker: MixpanelTrackerService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['data'] || changes?.['selectedMapType']) {
      this.lastValue = null;
      this.makeChart();
    }
  }

  ngOnInit(): void { }

  makeChart() {
    if (!(this.data?.length > 0)) {
      this.chartOption = null;
      return;
    }

    const lastValue = this.data[this.data.length - 1];
    if (this.activeTab === 'standard') {
      this.lastValue = {
        date: lastValue.date,
        value: lastValue.gddStandard,
      };
    } else {
      this.lastValue = {
        date: lastValue.date,
        value: lastValue.gddCropSpecific,
      };
    }

    const startDate = this.data[0].date.toJSDate();
    const endDate = this.data[this.data.length - 1].date.toJSDate();

    // Generate tick positions (1st day of each month between start and end)
    const ticks: Date[] = [];
    const current = new Date(startDate);
    current.setDate(1);

    while (current <= endDate) {
      ticks.push(new Date(current));
      current.setMonth(current.getMonth() + 1);
    }

    this.chartOption = {
      title: {
        show: false,
      },
      grid: {
        left: '50px',
        right: '50px',
        top: '10px',
        bottom: '50px',
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#f0f0f0',
            borderColor: '#bbb',
            borderWidth: 1,
            color: '#000',
          },
        },
        formatter: (params: any) => {
          const index = params[0].dataIndex;
          const date = this.luxonFormatPipe.transform(
            this.data[index].date,
            'yyyy-MM-dd'
          );

          const value = this.activeTab === 'standard'
            ? this.data[index].gddStandard
            : this.data[index].gddCropSpecific;

          return `<b>${date}</b><br/>${value}`;
        },
      },
      xAxis: {
        type: 'time',
        min: startDate,
        max: endDate,
        axisLabel: {
          formatter: (value: number) => {
            const date = new Date(value);
            const luxonDate = DateTime.fromJSDate(date);
            return this.luxonFormatPipe.transform(
              luxonDate,
              'LLL',
            );
          },
        },
        axisTick: {
          show: true,
        },
        splitLine: { show: false },
        splitNumber: ticks.length,
      },
      // 👇 inject custom tick positions
      xAxisTick: {
        show: true,
        interval: (index: number, value: any) => ticks.includes(value),
      },
      yAxis: {
        axisLine: {
          show: false,
        },
        axisLabel: {
          color: '#444',
        },
        splitLine: {
          lineStyle: {
            color: '#e0e0e0',
            type: 'dashed',
          },
        },
        splitNumber: 4,
        interval: 500,
        min: 0,
        // max: 1000,
      },
      series: [
        {
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: this.data.map((item) => [
            item.date.toJSDate(),
            this.activeTab === 'standard' ? item.gddStandard : item.gddCropSpecific,
          ]),
          lineStyle: {
            width: 5,
            color: this.selectedMapType?.key === 'ndmi' ? '#092abe' : '#68AB43', // Solid color
          },
          connectNulls: true,
        },
      ],
    };
  }

  onTabClick(tab: 'standard' | 'code-specific') {
    this.mixpanelTracker.trackServicesHeatAccumulationViewed({
      'chart type': tab === 'standard' ? 'Standard' : 'Crop Specific',
    });
    this.activeTab = tab;
    this.makeChart();
  }
}
